/**
 * 日落橙模版 - VIP会员开通页
 * 
 * 设计核心理念：横向对比表 + 3栏套餐卡片
 * 与teal（居中卡片Hero+Tab切换套餐）和red（深色渐变Hero+3栏并排）都不同：
 * - 顶部：金色华丽Hero区（VIP专属感）
 * - 中段：功能对比表（Free vs VIP 逐行对比，清晰直观）
 * - 底部：3栏套餐卡片（含推荐标签浮动效果）
 * - 金色点缀，奢华但不炫目
 */

import { useEffect, useState, useCallback } from "react";
import { Link } from "react-router-dom";
import Navbar from "@/templates/sunset/components/Navbar";
import Footer from "@/templates/sunset/components/Footer";
import { useSite } from "@/context/SiteContext";
import { useAuth } from "@/hooks/useAuth";
import { getVipPlans } from "@/utils/adminApi";

// ==================== API 层 ====================

interface PaymentConfig {
  alipay_enabled: boolean;
  wechat_enabled: boolean;
  qrcode_enabled: boolean;
  qrcode_priority: boolean;
  alipay_qrcode: string;
  wechat_qrcode: string;
}

async function fetchPaymentConfig(): Promise<PaymentConfig | null> {
  try {
    const token = localStorage.getItem("admin_token") || localStorage.getItem("token");
    const siteId = localStorage.getItem('current_site_id') || '';
    const res = await fetch("/api/admin/settings/payment", {
      method: "GET",
      headers: {
        "Authorization": token ? `Bearer ${token}` : "",
        'X-Site-Id': siteId,
      },
    });
    const data = await res.json();
    if (data.code === 0 && data.data) {
      return {
        alipay_enabled: !!data.data.alipay_enabled,
        wechat_enabled: !!data.data.wechat_enabled,
        qrcode_enabled: !!data.data.qrcode_enabled,
        qrcode_priority: !!data.data.qrcode_priority,
        alipay_qrcode: data.data.alipay_qrcode || "",
        wechat_qrcode: data.data.wechat_qrcode || "",
      };
    }
    return null;
  } catch (e) {
    console.error("读取支付配置失败", e);
    return null;
  }
}

export default function SunsetVipPage() {
  const { siteId, loading: siteLoading } = useSite();
  const { isLoggedIn } = useAuth();

  const [packages, setPackages] = useState<any[]>([]);
  const [loading, setLoading] = useState(true);
  const [selectedPlan, setSelectedPlan] = useState<number>(1);
  const [payMethod, setPayMethod] = useState<"wechat" | "alipay" | "qrcode">("wechat");
  const [showPayModal, setShowPayModal] = useState(false);
  const [paymentConfig, setPaymentConfig] = useState<PaymentConfig | null>(null);

  useEffect(() => {
    if (siteLoading || !siteId) return;
    Promise.all([
      getVipPlans().then(setPackages).catch(() => {}),
      fetchPaymentConfig().then(setPaymentConfig).catch(() => {}),
    ]).finally(() => setLoading(false));
  }, [siteId, siteLoading]);

  const handleBuy = useCallback((pkg: any) => {
    if (!isLoggedIn) {
      window.location.href = `/login?redirect=/vip`;
      return;
    }
    setSelectedPlan(pkg.id);
    setShowPayModal(true);
  }, [isLoggedIn]);

  // 支付方式判断
  const showAlipay = !!paymentConfig?.alipay_qrcode && (!!paymentConfig?.qrcode_enabled || !!paymentConfig?.alipay_enabled);
  const showWechat = !!paymentConfig?.wechat_qrcode && (!!paymentConfig?.qrcode_enabled || !!paymentConfig?.wechat_enabled);
  const currentPkg = displayPackages.find((p: any) => p.id === selectedPlan) || displayPackages[0];

  // 功能对比数据
  const compareFeatures = [
    { feature: "发布信息数", free: "5条/月", vip: "无限发布" },
    { feature: "置顶推荐", free: "❌", vip: "✅ 每日刷新" },
    { feature: "加V认证", free: "❌", vip: "✅ 官方认证" },
    { feature: "图片数量", free: "3张", vip: "20张" },
    { feature: "联系方式", free: "基础", vip: "完整展示" },
    { feature: "数据统计", free: "❌", vip: "✅ 详细报表" },
    { feature: "优先客服", free: "❌", vip: "✅ 专属通道" },
    { feature: "去广告", free: "❌", vip: "✅ 纯净体验" },
  ];

  const displayPackages = packages.length > 0 ? packages : [
    { id: 1, name: "月度VIP", price: "29.9", duration: 30, description: "适合短期需求", is_recommended: false },
    { id: 2, name: "季度VIP", price: "79.9", duration: 90, description: "性价比之选", is_recommended: true },
    { id: 3, name: "年度VIP", price: "239.9", duration: 365, description: "最高性价比", is_recommended: false },
  ];

  return (
    <div className="min-h-screen bg-[#fffbf5]">
      <Navbar />

      {/* ── Hero 区：金色华丽感 ── */}
      <section className="relative bg-gradient-to-br from-amber-600 via-orange-500 to-rose-500 pt-20 pb-16 overflow-hidden">
        {/* 装饰 */}
        <div className="absolute inset-0">
          <div className="absolute top-10 right-10 w-72 h-72 rounded-full bg-white/5 blur-3xl" />
          <div className="absolute -bottom-20 -left-20 w-96 h-96 rounded-full bg-amber-300/10 blur-3xl" />
          <div className="absolute top-1/2 left-1/4 w-4 h-4 rounded-full bg-white/20" />
          <div className="absolute top-1/4 right-1/3 w-3 h-3 rounded-full bg-white/15" />
          <div className="absolute bottom-1/3 right-1/4 w-5 h-5 rounded-full bg-amber-200/30" />
        </div>

        <div className="relative z-10 max-w-4xl mx-auto px-4 text-center">
          <div className="inline-flex items-center gap-2 px-4 py-1.5 bg-white/15 backdrop-blur-sm rounded-full text-white/90 text-xs font-semibold mb-6">
            <i className="ri-vip-crown-line text-amber-200" />
            VIP 会员专属特权
          </div>
          <h1 className="text-3xl sm:text-4xl md:text-5xl font-extrabold text-white mb-4 leading-tight">
            升级<span className="text-amber-200">VIP</span>，获得更多特权
          </h1>
          <p className="text-white/80 text-base sm:text-lg max-w-2xl mx-auto leading-relaxed">
            置顶推荐、无限发布、加V认证，让您的信息获得更多曝光
          </p>

          {/* 统计 */}
          <div className="grid grid-cols-3 gap-4 max-w-lg mx-auto mt-10">
            {[
              { value: "10x", label: "曝光提升" },
              { value: "5万+", label: "活跃用户" },
              { value: "98%", label: "满意度" },
            ].map((stat, idx) => (
              <div key={idx} className="text-center">
                <div className="text-2xl sm:text-3xl font-bold text-amber-200">{stat.value}</div>
                <div className="text-xs text-white/60 mt-1">{stat.label}</div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* ── 功能对比表 ── */}
      <section className="relative -mt-6 z-10 pb-8">
        <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="bg-white rounded-3xl shadow-xl shadow-amber-500/5 border border-amber-100/50 overflow-hidden">
            <div className="p-6 sm:p-8 border-b border-gray-100">
              <h2 className="text-xl font-bold text-gray-800 text-center mb-1">功能对比</h2>
              <p className="text-sm text-gray-400 text-center">免费用户 vs VIP 会员</p>
            </div>

            <div className="overflow-x-auto">
              <table className="w-full">
                <thead>
                  <tr className="bg-gray-50/50">
                    <th className="text-left px-6 py-4 text-sm font-bold text-gray-700">功能</th>
                    <th className="text-center px-6 py-4 text-sm font-semibold text-gray-400">
                      <i className="ri-user-line mr-1" />免费用户
                    </th>
                    <th className="text-center px-6 py-4 text-sm font-semibold text-amber-600">
                      <i className="ri-vip-crown-line mr-1" />VIP会员
                    </th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-gray-50">
                  {compareFeatures.map((row, idx) => (
                    <tr key={idx} className="hover:bg-amber-50/20 transition-colors">
                      <td className="px-6 py-4 text-sm text-gray-600 font-medium">{row.feature}</td>
                      <td className="px-6 py-4 text-sm text-gray-400 text-center">{row.free}</td>
                      <td className="px-6 py-4 text-sm text-amber-700 text-center font-medium">{row.vip}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        </div>
      </section>

      {/* ── 套餐卡片 ── */}
      <section className="pb-16">
        <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-10">
            <h2 className="text-2xl font-bold text-gray-800 mb-2">选择套餐</h2>
            <p className="text-gray-400 text-sm">选择适合您的VIP方案</p>
          </div>

          {loading ? (
            <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
              {[...Array(3)].map((_, i) => (
                <div key={i} className="animate-pulse bg-white rounded-3xl border border-gray-100 p-8">
                  <div className="w-1/2 h-6 bg-gray-200 rounded mx-auto mb-4" />
                  <div className="w-1/3 h-10 bg-gray-200 rounded mx-auto mb-6" />
                  <div className="space-y-3">
                    {[...Array(5)].map((_, j) => (
                      <div key={j} className="w-full h-4 bg-gray-100 rounded" />
                    ))}
                  </div>
                </div>
              ))}
            </div>
          ) : (
            <div className="grid grid-cols-1 md:grid-cols-3 gap-6 max-w-4xl mx-auto">
              {displayPackages.map((pkg, idx) => {
                const isRecommended = pkg.is_recommended;
                return (
                  <div
                    key={pkg.id}
                    className={`relative bg-white rounded-3xl border-2 transition-all duration-300 flex flex-col ${
                      isRecommended
                        ? "border-amber-400 shadow-2xl shadow-amber-500/15 scale-[1.03] z-10"
                        : "border-gray-100 shadow-lg hover:border-amber-200 hover:shadow-xl"
                    }`}
                  >
                    {isRecommended && (
                      <div className="absolute -top-4 inset-x-0 flex justify-center">
                        <span className="px-5 py-1 bg-gradient-to-r from-amber-400 to-orange-500 text-white text-xs font-bold rounded-full shadow-lg shadow-amber-500/30 flex items-center gap-1">
                          <i className="ri-star-line text-[10px]" />热门推荐
                        </span>
                      </div>
                    )}

                    <div className="p-8 flex-1 flex flex-col">
                      <div className="text-center mb-6">
                        <h3 className="text-lg font-bold text-gray-800 mb-1">{pkg.name}</h3>
                        <p className="text-xs text-gray-400">{pkg.description}</p>
                      </div>

                      <div className="text-center mb-6">
                        <div className="flex items-baseline justify-center gap-1">
                          <span className="text-sm text-gray-400">¥</span>
                          <span className={`text-4xl font-extrabold ${isRecommended ? 'text-amber-600' : 'text-gray-800'}`}>
                            {pkg.price}
                          </span>
                        </div>
                        <p className="text-xs text-gray-400 mt-1">
                          {pkg.duration >= 365 ? `仅 ¥${(Number(pkg.price) / 12).toFixed(1)}/月` : `共 ${pkg.duration} 天`}
                        </p>
                      </div>

                      <ul className="space-y-3 mb-8 flex-1">
                        {[
                          "无限发布信息",
                          "每日置顶推荐",
                          "加V官方认证",
                          "20张高清图片",
                          "联系方式完整展示",
                        ].map((feature, fi) => (
                          <li key={fi} className="flex items-start gap-2.5">
                            <i className={`ri-check-line text-sm mt-0.5 ${isRecommended ? 'text-amber-500' : 'text-green-500'}`} />
                            <span className="text-sm text-gray-600">{feature}</span>
                          </li>
                        ))}
                      </ul>

                      <button
                        onClick={() => handleBuy(pkg)}
                        className={`w-full py-3.5 rounded-2xl font-bold text-sm transition-all ${
                          isRecommended
                            ? "bg-gradient-to-r from-amber-500 to-orange-600 text-white shadow-lg shadow-amber-500/25 hover:from-amber-600 hover:to-orange-700"
                            : "border-2 border-amber-200 text-amber-600 hover:bg-amber-50"
                        }`}
                      >
                        立即开通
                      </button>
                    </div>
                  </div>
                );
              })}
            </div>
          )}

          {/* 底部说明 */}
          <div className="mt-10 text-center">
            <p className="text-xs text-gray-400">
              开通即表示同意{" "}
              <Link to="/page/register-agreement" className="text-amber-500 hover:text-amber-600">服务协议</Link>
              {" "}和{" "}
              <Link to="/page/privacy" className="text-amber-500 hover:text-amber-600">隐私政策</Link>
            </p>
          </div>
        </div>
      </section>

      <Footer />

      {/* ── 支付弹窗 ── */}
      {showPayModal && (
        <div className="fixed inset-0 bg-black/50 backdrop-blur-sm flex items-center justify-center z-50 px-4">
          <div className="bg-white rounded-2xl p-6 w-full max-w-sm shadow-2xl">
            <div className="flex items-center justify-between mb-5">
              <h3 className="font-bold text-gray-800 text-lg">扫码支付</h3>
              <button
                onClick={() => setShowPayModal(false)}
                className="w-8 h-8 flex items-center justify-center rounded-full hover:bg-gray-100 transition-colors"
              >
                <i className="ri-close-line text-gray-500" />
              </button>
            </div>

            {/* 支付方式切换 */}
            <div className="flex gap-2 mb-4">
              {showWechat && (
                <button
                  onClick={() => setPayMethod("wechat")}
                  className={`flex-1 flex items-center justify-center gap-1.5 py-2 rounded-xl text-xs font-medium transition-all ${
                    payMethod === "wechat"
                      ? "bg-green-50 text-green-700 border-2 border-green-400"
                      : "bg-gray-50 text-gray-500 border-2 border-transparent hover:border-gray-200"
                  }`}
                >
                  <i className="ri-wechat-pay-fill" />微信
                </button>
              )}
              {showAlipay && (
                <button
                  onClick={() => setPayMethod("alipay")}
                  className={`flex-1 flex items-center justify-center gap-1.5 py-2 rounded-xl text-xs font-medium transition-all ${
                    payMethod === "alipay"
                      ? "bg-blue-50 text-blue-700 border-2 border-blue-400"
                      : "bg-gray-50 text-gray-500 border-2 border-transparent hover:border-gray-200"
                  }`}
                >
                  <i className="ri-alipay-fill" />支付宝
                </button>
              )}
            </div>

            <div className="text-center">
              {/* 支付宝收款码 */}
              {payMethod === "alipay" && showAlipay && (
                <div>
                  <p className="text-xs text-gray-500 mb-3">请使用支付宝扫码付款</p>
                  <img
                    src={paymentConfig?.alipay_qrcode}
                    alt="支付宝收款码"
                    className="w-52 h-52 mx-auto rounded-xl border border-gray-200"
                  />
                </div>
              )}

              {/* 微信收款码 */}
              {payMethod === "wechat" && showWechat && (
                <div>
                  <p className="text-xs text-gray-500 mb-3">请使用微信扫码付款</p>
                  <img
                    src={paymentConfig?.wechat_qrcode}
                    alt="微信收款码"
                    className="w-52 h-52 mx-auto rounded-xl border border-gray-200"
                  />
                </div>
              )}

              {/* 无收款码占位 */}
              {!showAlipay && !showWechat && (
                <>
                  <div className="w-44 h-44 mx-auto mb-4 bg-gray-100 rounded-xl flex items-center justify-center">
                    <i className="ri-qr-code-line text-5xl text-gray-300" />
                  </div>
                  <p className="text-sm text-gray-500 mb-1">支付功能暂未启用</p>
                  <p className="text-xs text-gray-400">请联系管理员配置收款码</p>
                </>
              )}

              <p className="text-2xl font-bold text-amber-600 mt-4 mb-1">
                ¥{currentPkg?.price || "29.9"}
              </p>
              <p className="text-xs text-gray-400">
                {currentPkg?.name || "VIP套餐"} · {currentPkg?.duration || 30}天
              </p>
            </div>

            <div className="mt-5 p-3.5 bg-amber-50 rounded-xl text-xs text-amber-700 text-center font-medium">
              <i className="ri-information-line mr-1" />
              支付成功后将自动开通VIP权益
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
